import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { hasActiveRun } from '@/lib/active-run';
import { isToolId } from '@/components/landing/ToolCompass';
import { FAQSection } from '@/components/landing/FAQSection';
import { HeroSection, pickInitialTool } from '@/components/landing/HeroSection';
import type { ToolId } from '@/components/landing/ToolCompass';
import { CreditPacksSection } from '@/components/landing/CreditPacksSection';
import { ContinueLink, type ContinueItem } from '@/components/home/HomeDoors';
import { CreditsBand } from '@/components/home/CreditsBand';
import { MagneticCursor } from '@/components/fx/MagneticCursor';
import { MadeHere } from '@/components/home/MadeHere';
import { ApprovalRail } from '@/components/trust/ApprovalRail';
import { SupportLine } from '@/components/trust/SupportLine';
import { isCreditsVisit } from '@/hooks/useCreditCheckout';
import { canAnimate } from '@/components/landing/immersive/heroEnv';
import { trackPageView } from '@/lib/analytics';
import { getGsap, prefersReducedMotion } from '@/lib/motion';
import { isHistoryRestore } from '@/lib/scroll-memory';
import { editorLang, editorPath } from '@/components/editorPages/routes';
import { listSavedStorybooks } from '@/lib/storybook-api';
import { DOOR_STUDIOS, type StudioId } from '@/lib/studios';

// HOME IS A DIRECTORY (Studio Blueprint 02, 2026-10-16): the promise, four
// labelled doors, the free-editor strip and a row of the five quick tools, then
// real outputs, how it works, three credit packs (slim rows) and five questions:
// about five phone screens (it was 15). Each
// studio lives on its own page now (/music, /ads, /tiktok, /storybook,
// /editor, /tools), so home never mounts the Director console.
//
// OLD LINKS STILL LAND: /?tool=<stop>, /#director-console, ?resume_corrido=
// and ?resume_corrido_job= (completion emails, the gallery, the board's live
// card), /#storybook and /#chopbox go to that studio's page with the rest of
// the search and the hash kept. A visit that came to buy credits (#credits,
// ?pack=, ?from=studio|bot, a cancelled checkout) stays here on the packs.
const CORRIDO_HASHES = new Set(['#director-console', '#why-corrido', '#compare']);
const STOP_PATH: Record<string, string> = { corrido: '/music', rap: '/music', story: '/tiktok', ads: '/ads', storybook: '/storybook' };

/** Reel items the smoke stage's tool cards already play (ToolChooser's clips). */
const ON_STAGE = ['belico', 'storybook', 'energy', 'story', 'qt-captions', 'qt-faceless', 'qt-hooks', 'qt-canvas', 'qt-vows'] as const;

export function legacyStudioTarget(search: string, hash: string, lang = 'en'): string | null {
  if (isCreditsVisit(search, hash)) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return null;
  }
  const asked = (params.get('tool') || '').trim().toLowerCase();
  const anchor = (hash || '').toLowerCase();
  let stop: string | null = isToolId(asked) ? asked : null;
  if (!stop && (params.has('resume_corrido') || params.has('resume_corrido_job'))) stop = 'corrido';
  if (!stop && CORRIDO_HASHES.has(anchor)) stop = 'corrido';
  if (!stop && anchor === '#storybook') stop = 'storybook';
  if (!stop && anchor === '#chopbox') stop = 'chopbox';
  if (!stop || stop === 'welcome') return null;
  if (stop === 'chopbox') return editorPath('hub', editorLang(lang));
  params.delete('tool');
  const rest = params.toString();
  const keepHash = stop === 'storybook' ? '' : hash;
  return STOP_PATH[stop] + (rest ? '?' + rest : '') + (keepHash || '');
}

/** ?door=<studio>, or a utm_content that names one: that door opens first. */
function doorFrom(search: string): StudioId | null {
  const q = new URLSearchParams(search || '');
  const asked = (q.get('door') || q.get('utm_content') || '').trim().toLowerCase();
  return (DOOR_STUDIOS as readonly string[]).includes(asked) ? (asked as StudioId) : null;
}

// ScrollTrigger, or null where GSAP can't start (jsdom in tests) or nothing
// on the page animates (reduced motion: no pins, nothing to refresh). Checked
// first, as HeroSection does (2026-09-25): registering in jsdom threw only
// after it had woken GSAP's ticker; the try/catch stays as a second line.
function scrollTriggerOrNull() {
  if (!canAnimate()) return null;
  try {
    return getGsap().ScrollTrigger;
  } catch {
    return null;
  }
}

export default function Index() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const legacy = legacyStudioTarget(location.search, location.hash, i18n.language);
  const door = doorFrom(location.search);
  // A visit that came to buy credits still lands on the full pack list.
  const creditsVisit = isCreditsVisit(location.search, location.hash);
  // The "how it works" story belongs to the hero's welcome stop only, as before.
  const [, setOnWelcome] = useState(() => pickInitialTool(location.search, location.hash) === 'welcome');
  const onToolChange = useCallback((stop: ToolId) => setOnWelcome(stop === 'welcome'), []);

  // Work in progress on this device: a corrido render still running, or the
  // last storybook made here. One Continue line above the doors.
  const resume = useMemo<ContinueItem | null>(() => {
    if (hasActiveRun()) return { label: t('home.continueMusic'), to: '/music#director-console' };
    const books = listSavedStorybooks();
    if (books.length) return { label: t('home.continueBook'), to: '/storybook/' + books[0].id };
    return null;
  }, [t]);

  // Which location the hash effect last ran for, and whether that run was an
  // arrival. Keyed by location.key so a re-run for the same entry (React's
  // dev double effects) is not mistaken for a click on this page.
  const lastRunRef = useRef<{ key: string; arrival: boolean } | null>(null);

  useEffect(() => {
    const last = lastRunRef.current;
    // The first run is an arrival: a cold load, or a link from another page.
    // Every later one is a click on this page (navbar, menu, footer).
    const arrival = last ? last.key === location.key && last.arrival : true;
    lastRunRef.current = { key: location.key, arrival };

    // An old studio link: the page is about to move to that studio.
    if (legacy) return undefined;

    // Back / Forward: ScrollManager puts the page where the visitor left it;
    // the top or the #hash here would undo that (2026-10-02).
    if (isHistoryRestore(location)) return undefined;

    // The credits section scrolls its own visits (Buy credits from Telegram,
    // a pick, a cancelled checkout) once its packs are in; scrolling here too
    // would pull the page back to the top or to the heading, and the re-align
    // below would keep fighting it for 15s.
    if (isCreditsVisit(location.search, location.hash)) return undefined;

    if (!location.hash) {
      // Arriving from another page: straight to the top. A smooth scroll up
      // from where that page was left gets cut short by the ScrollTriggers'
      // first refresh (below).
      window.scrollTo({ top: 0, behavior: arrival ? 'instant' : 'smooth' });
      return undefined;
    }

    const targetId = location.hash.replace('#', '');
    const jump = () => document.getElementById(targetId)?.scrollIntoView({ behavior: 'instant', block: 'start' });
    const ScrollTrigger = scrollTriggerOrNull();

    // A glide in flight: re-aligning now would cut it short, so every
    // re-align waits for it to end (below).
    let gliding = false;

    // Arrivals and long trips jump; only a short hop on this page glides
    // (2026-09-25). ScrollTrigger's refresh (the pinned "how it works", the
    // hero's fly-through, fired as they are created, on window load and when
    // the layout above them settles) scrolls the page to 0 to measure and
    // back, and that kills a smooth scroll in flight: /#faq from another
    // page sat on the hero for seconds. And a ~7,000px smooth scroll on a
    // phone outlasted the old fixed-delay re-align, which then landed
    // mid-flight and overshot the FAQ by up to 1,000px.
    const element = document.getElementById(targetId);
    if (element instanceof HTMLElement) {
      const near = Math.abs(element.getBoundingClientRect().top) <= window.innerHeight * 2;
      if (!arrival && near && !prefersReducedMotion()) {
        gliding = true;
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Measure with every pin in place, so the jump lands the first time.
        try {
          if (ScrollTrigger && ScrollTrigger.getAll().length) ScrollTrigger.refresh();
        } catch { /* nothing to refresh */ }
        element.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    }

    // Then keep the target lined up while the page settles: whenever the
    // page changes height (media, a stop's content, a pin spacer; a
    // ResizeObserver runs before paint, so the shift never shows), after each
    // ScrollTrigger refresh, once the web fonts land (they reflow everything
    // above it), and 900ms after that, when the console's 0.25s delay + 0.6s
    // fade-up has ended (/#director-console from completion emails, /corrido
    // and the gallery's resume link). Instant, because html has
    // scroll-behavior:smooth. All of it stops the moment the visitor scrolls,
    // taps or types (from then on they're driving) and after 15s either way.
    let alive = true;
    let glideTimer: number | undefined;
    let glideCap: number | undefined;
    let fontsTimer: number | undefined;
    let capTimer: number | undefined;
    let pageResize: ResizeObserver | null = null;
    const intents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    const realign = () => {
      if (alive && !gliding) jump();
    };
    // The glide is over at 'scrollend'. Not "no scroll event for a while":
    // on a busy main thread the events stall mid-glide, and a re-align on
    // that silence cut the glide short. Where 'scrollend' doesn't exist
    // (older Safari), 250ms without one after the glide has moved at all.
    // Either way it ends in a re-align: a glide cut short by a single
    // trigger's refresh (that fires no 'refresh' event) finishes as a jump.
    const endGlide = () => {
      if (!gliding) return;
      gliding = false;
      window.clearTimeout(glideTimer);
      window.clearTimeout(glideCap);
      realign();
    };
    const hasScrollEnd = 'onscrollend' in window;
    const onScroll = () => {
      if (!gliding || hasScrollEnd) return;
      window.clearTimeout(glideTimer);
      glideTimer = window.setTimeout(endGlide, 250);
    };
    const stop = () => {
      alive = false;
      gliding = false;
      window.clearTimeout(glideTimer);
      window.clearTimeout(glideCap);
      window.clearTimeout(fontsTimer);
      window.clearTimeout(capTimer);
      pageResize?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', endGlide);
      intents.forEach((type) => window.removeEventListener(type, stop));
      try { ScrollTrigger?.removeEventListener('refresh', realign); } catch { /* gone */ }
    };
    intents.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
    if (gliding) {
      window.addEventListener('scroll', onScroll, { passive: true });
      if (hasScrollEnd) window.addEventListener('scrollend', endGlide);
      // A glide that never starts (already in place) fires neither.
      glideCap = window.setTimeout(endGlide, 2500);
    }
    try { ScrollTrigger?.addEventListener('refresh', realign); } catch { /* no ScrollTrigger */ }
    if (typeof ResizeObserver !== 'undefined') {
      pageResize = new ResizeObserver(realign);
      pageResize.observe(document.body);
    }
    capTimer = window.setTimeout(stop, 15000);

    let fontsReady: Promise<unknown> = Promise.resolve();
    try {
      fontsReady = document.fonts?.ready ?? Promise.resolve();
    } catch { /* no Font Loading API: settle on the timer alone */ }
    void fontsReady.catch(() => undefined).then(() => {
      if (!alive) return;
      realign();
      fontsTimer = window.setTimeout(realign, 900);
    });
    return stop;
  }, [location, legacy]);

  useEffect(() => {
    if (legacy) return;
    trackPageView('landing', { hash: location.hash || null, door });
  }, [location.hash, legacy, door]);

  if (legacy) return <Navigate to={legacy} replace />;

  return (
    // overflow-x-clip, not -hidden (2026-09-24): hidden makes this div a
    // scroll container, which breaks position: sticky and GSAP pinning for
    // everything inside it. clip trims the sideways overflow the same way.
    <div className="min-h-screen bg-background overflow-x-clip">
      <Navbar />
      {/* HOME IS A DIRECTORY (Studio Blueprint 02; owner 2026-10-16: "the
          same content just repeats", "the prices were supposed to be on a
          completely different page"). Each thing appears once, in the
          blueprint's order, under the smoke stage (owner: never touched):
            1. the stage with its labelled tool cards: these ARE the doors
               (the separate door grid showed the same pictures again);
            2. Made here: real outputs, a tap opens the Reel;
            3. how it works: the approval rail;
            4. one balance: a line about credits and a link to /pricing,
               where every pack and price now lives;
            5. questions and the human support line.
          The quick tools are one of the stage's cards (with every price)
          and live on /tools; Made here plays their samples.
          The eight-pack wall only shows for a visit that came to buy
          credits (#credits, ?pack=, back from Stripe or the Telegram
          studio), so those flows land on the packs as before. */}
      <main>
        <HeroSection onToolChange={onToolChange} />
        {resume && <ContinueLink resume={resume} />}
        {creditsVisit && <CreditPacksSection />}
        <MadeHere door={door} skip={ON_STAGE} />
        <ApprovalRail kind="studio" className="px-4 py-10 md:px-8" />
        {!creditsVisit && <CreditsBand />}
        <FAQSection />
        <SupportLine where="home" className="container mx-auto px-4 pb-14" />
      </main>
      <Footer />
      {/* The pointer light that goes with the smoke stage, as before. */}
      <MagneticCursor />
    </div>
  );
}
